import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { useTranslation } from "react-i18next";
import { getCategoryFields, sendRequest } from "../request-orders.service";
import { toast } from "react-toastify";
import CloseIcon from "shared/assets/images/svg/close_form.svg";
import "../requestOrders.scss";

const NewRequestModal = ({ show, onHide, categoryId, onCreated }) => {
  const { t } = useTranslation();
  const [fields, setFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [fileData, setFileData] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (show && categoryId) {
      setLoadingFields(true);
      getCategoryFields(categoryId)
        .then((data) => {
          setFields(data);
          const initial = {};
          data.forEach((f) => {
            const multiple = f.isMultiple || f.IsMultiple;
            initial[f.id] = multiple ? [] : "";
          });
          setFormData(initial);
          setFileData({});
          setErrors({});
        })
        .catch(() => {})
        .finally(() => setLoadingFields(false));
    }
  }, [show, categoryId]);

  const handleChange = (fieldId, value) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
    if (errors[fieldId]) {
      setErrors((prev) => ({ ...prev, [fieldId]: null }));
    }
  };

  const handleFileChange = (fieldId, files, isMultiple) => {
    if (!files || files.length === 0) return;

    if (isMultiple) {
      const newFiles = Array.from(files);
      setFileData((prev) => ({
        ...prev,
        [fieldId]: [...(prev[fieldId] || []), ...newFiles],
      }));
      const readers = newFiles.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve([file.name, reader.result.split(",")[1]]);
            reader.readAsDataURL(file);
          })
      );
      Promise.all(readers).then((results) => {
        setFormData((prev) => ({
          ...prev,
          [fieldId]: [...(prev[fieldId] || []), ...results],
        }));
      });
    } else {
      const file = files[0];
      setFileData((prev) => ({ ...prev, [fieldId]: file }));
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        handleChange(fieldId, [file.name, base64]);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = (fieldId, index) => {
    if (index !== undefined) {
      setFileData((prev) => ({
        ...prev,
        [fieldId]: (prev[fieldId] || []).filter((_, i) => i !== index),
      }));
      setFormData((prev) => ({
        ...prev,
        [fieldId]: (prev[fieldId] || []).filter((_, i) => i !== index),
      }));
    } else {
      setFileData((prev) => ({ ...prev, [fieldId]: null }));
      handleChange(fieldId, "");
    }
  };

  const validate = () => {
    const newErrors = {};
    fields.forEach((f) => {
      const mandatory = f.isMandatory || f.IsMandatory;
      if (mandatory) {
        const val = formData[f.id];
        if (!val || (Array.isArray(val) && val.length === 0) || val === "") {
          newErrors[f.id] = t("request-orders.required");
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await sendRequest(categoryId, formData);
      toast.success(t("form.save-success"));
      onCreated();
    } catch {
      // error handled by client
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field) => {
    const type = (field.dataType || field.DataType || "").toLowerCase();

    switch (type) {
      case "enumeration": {
        const multiple = field.isMultiple || field.IsMultiple;

        if (multiple) {
          return (
            <MultiSelect
              options={field.lstofdata || []}
              value={Array.isArray(formData[field.id]) ? formData[field.id] : []}
              onChange={(vals) => handleChange(field.id, vals)}
            />
          );
        }

        return (
          <select
            className="form-select"
            value={formData[field.id] || ""}
            onChange={(e) => handleChange(field.id, e.target.value)}
          >
            <option value="">{t("common.select-all")}</option>
            {(field.lstofdata || []).map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.name}
              </option>
            ))}
          </select>
        );
      }

      case "integer":
      case "double":
        return (
          <input
            type="number"
            className="form-control"
            value={formData[field.id] || ""}
            onChange={(e) => handleChange(field.id, e.target.value)}
          />
        );

      case "file": {
        const multiple = field.isMultiple || field.IsMultiple;
        const currentFiles = fileData[field.id];

        if (multiple) {
          const fileList = Array.isArray(currentFiles) ? currentFiles : [];
          return (
            <div className="file-field">
              {fileList.length > 0 && (
                <div className="file-list">
                  {fileList.map((f, i) => (
                    <div key={i} className="file-selected">
                      <span className="file-name">{f.name}</span>
                      <button
                        type="button"
                        className="remove-file-btn"
                        onClick={() => removeFile(field.id, i)}
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <FileInput
                multiple
                onChange={(files) => handleFileChange(field.id, files, true)}
              />
            </div>
          );
        }

        return (
          <div className="file-field">
            {currentFiles ? (
              <div className="file-selected">
                <span className="file-name">{currentFiles.name}</span>
                <button
                  type="button"
                  className="remove-file-btn"
                  onClick={() => removeFile(field.id)}
                >
                  &times;
                </button>
              </div>
            ) : (
              <FileInput
                onChange={(files) => handleFileChange(field.id, files, false)}
              />
            )}
          </div>
        );
      }

      case "date":
      case "datetime":
        return (
          <input
            type="date"
            className="form-control"
            value={formData[field.id] || ""}
            onChange={(e) => handleChange(field.id, e.target.value)}
          />
        );

      case "boolean":
        return (
          <div className="form-check form-switch">
            <input
              type="checkbox"
              className="form-check-input"
              checked={!!formData[field.id]}
              onChange={(e) => handleChange(field.id, e.target.checked ? "1" : "0")}
            />
          </div>
        );

      case "string":
      default: {
        const rows = Number(field.rows || field.Rows || 1);

        if (rows > 1) {
          return (
            <textarea
              className="form-control memo-field"
              rows={rows}
              value={formData[field.id] || ""}
              onChange={(e) => handleChange(field.id, e.target.value)}
            />
          );
        }

        return (
          <input
            type="text"
            className="form-control"
            value={formData[field.id] || ""}
            onChange={(e) => handleChange(field.id, e.target.value)}
          />
        );
      }
    }
  };

  return (
    <React.Fragment>
      <SwipeableDrawer
        anchor="right"
        open={show}
        onClose={onHide}
        onOpen={() => {}}
      >
        <Box role="presentation" className="newRequestDrawer">
          <div className="row">
            <div className="col-12">
              <div className="drawerHeader">
                <div className="info">
                  <h2>{t("request-orders.newRequest")}</h2>
                </div>
                <div className="action">
                  <img src={CloseIcon} alt="close" onClick={onHide} />
                </div>
              </div>
            </div>
          </div>

          <div className="newRequestDrawer__body">
            {loadingFields ? (
              <div className="loading-spinner">
                <div className="spinner-border text-primary" role="status" />
              </div>
            ) : (
              <div className="drawer-fields">
                {fields.map((field) => {
                  const mandatory = field.isMandatory || field.IsMandatory;
                  return (
                    <div key={field.id} className="form-group form-div mt-0 row g-3 align-items-center">
                      <label className="col-lg-4 col-form-label">
                        {field.name}
                        {mandatory && <small className="star-require"> *</small>}
                      </label>
                      <div className="col-lg-8">
                        {renderField(field)}
                        {errors[field.id] && (
                          <small className="text-danger">{errors[field.id]}</small>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="newRequestDrawer__footer">
            <button
              className="btn-blue"
              onClick={handleSubmit}
              disabled={loading || loadingFields}
            >
              {loading ? "..." : t("request-orders.submit")}
            </button>
            <button className="btn-border-blue" onClick={onHide}>
              {t("request-orders.cancel")}
            </button>
          </div>
        </Box>
      </SwipeableDrawer>
    </React.Fragment>
  );
};

const MultiSelect = ({ options, value, onChange }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (id) => {
    const sid = String(id);
    if (value.includes(sid)) {
      onChange(value.filter((v) => v !== sid));
    } else {
      onChange([...value, sid]);
    }
  };

  const selected = options.filter((o) => value.includes(String(o.id)));

  return (
    <div className="multi-select" ref={ref}>
      <div
        className="multi-select__control"
        onClick={() => setOpen((prev) => !prev)}
      >
        {selected.length > 0 ? (
          <div className="multi-select__tags">
            {selected.map((opt) => (
              <span key={opt.id} className="multi-select__tag">
                {opt.name}
                <button
                  type="button"
                  className="multi-select__tag-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(opt.id);
                  }}
                >
                  &times;
                </button>
              </span>
            ))}
          </div>
        ) : (
          <span className="multi-select__placeholder">
            {t("common.select-all")}
          </span>
        )}
        <i className="fas fa-chevron-down multi-select__arrow" />
      </div>

      {open && (
        <div className="multi-select__menu">
          {options.length === 0 ? (
            <div className="multi-select__empty">{t("common.no-data")}</div>
          ) : (
            options.map((opt) => (
              <label key={opt.id} className="multi-select__option">
                <input
                  type="checkbox"
                  checked={value.includes(String(opt.id))}
                  onChange={() => toggle(opt.id)}
                />
                <span>{opt.name}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const FileInput = ({ onChange, multiple }) => {
  const { t } = useTranslation();
  const ref = useRef(null);

  return (
    <div className="file-upload-area" onClick={() => ref.current?.click()}>
      <input
        type="file"
        ref={ref}
        multiple={multiple}
        style={{ display: "none" }}
        onChange={(e) => {
          onChange(e.target.files);
          e.target.value = "";
        }}
      />
      <span className="file-upload-text">
        <i className="fas fa-cloud-upload-alt" /> {t("request-orders.attachFile")}
      </span>
    </div>
  );
};

export default NewRequestModal;
